/**
 * 개선 전/후 실행계획을 **나란히** 본다.
 *
 * 에이전트가 `benchmark_sql` 로 결론을 내면 이 탭이 뜬다(SSE `plan_compare`). 판정 근거를
 * 채팅 글로만 주면 사용자가 두 계획을 견줄 수가 없다.
 *
 * 좌우는 기존 `SplitView` 를 `renderClone` 으로 재사용한다 — 드래그 리사이즈·CSS 가 그대로 온다.
 * 자식에는 **`isResultSplit={false}` 를 넘긴다**: 안 넘기면 `InteractivePlanContent` 안의 또
 * 다른 SplitView 가 켜져 4칸이 된다.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiBookmark, FiAlertTriangle } from 'react-icons/fi';
import SplitView from './SplitView';
import InteractivePlanContent from './InteractivePlanContent';
import styles from './PlanCompare.module.css';

/** 보관·내보내기가 기대하는 탭 모양으로 한쪽을 감싼다 */
const sideToTab = (side, id, connectionId) => ({
    id,
    name: (side && side.label) || id,
    data: (side && side.planText) || '',
    query: (side && side.sql) || '',
    connectionId: connectionId || null,
});

/** 없는 값은 0 이 아니라 '-' 다 — 작은 값이 0 으로 뭉개지면 "읽기가 없었다" 로 읽힌다 */
const num = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toLocaleString());

function PlanCompare({ tab, statsConnectionId, onPlanNodeClick, onShowTableDDL, showPlanObjectDetail, onSaveToArchive }) {
    const { t } = useTranslation();
    const all = (tab.compare && tab.compare.all) || [];
    const [pickedLabel, setPickedLabel] = useState(null);

    const before = tab.compare.before;
    // 머리글 — 기본은 「개선 전 / 개선 후」(에이전트). 튜닝 챌린지는 「모범답안 / 내 제출」 을 넘긴다(2026-10-05)
    const labels = tab.compare.labels || {};
    const after = useMemo(() => {
        if (pickedLabel) return all.find(r => r.label === pickedLabel) || tab.compare.after;
        return tab.compare.after;
    }, [pickedLabel, all, tab.compare.after]);

    const bm = (before && before.metrics) || {};
    const am = (after && after.metrics) || {};
    // 논리 읽기가 판정 지표다(시간은 캐시 덕을 본다)
    const delta = (bm.buffers != null && am.buffers != null && bm.buffers > 0)
        ? Math.round(((am.buffers - bm.buffers) / bm.buffers) * 100)
        : null;

    const beforeTab = sideToTab(before, `${tab.id}_before`, tab.connectionId);
    const afterTab = sideToTab(after, `${tab.id}_after`, tab.connectionId);

    const child = (which) => (
        <InteractivePlanContent
            activeTab={which}
            statsConnectionId={statsConnectionId}
            onPlanNodeClick={onPlanNodeClick}
            onShowTableDDL={onShowTableDDL}
            showPlanObjectDetail={showPlanObjectDetail}
            isResultSplit={false}
        />
    );

    return (
        <div className={styles.compareRoot}>
            <div className={styles.compareHeader}>
                <div className={styles.verdict}>
                    <span className={styles.sideLabel}>{labels.before || t('common:planCompare.before')}</span>
                    <code>{num(bm.buffers)}</code>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.sideLabel}>{labels.after || t('common:planCompare.after')}</span>
                    <code>{num(am.buffers)}</code>
                    {delta != null && (
                        <span className={`${styles.delta} ${delta < 0 ? styles.better : styles.worse}`}>
                            {delta > 0 ? `+${delta}` : delta}%
                        </span>
                    )}
                    <span className={styles.metricNote}>{t('common:planCompare.buffersNote')}</span>
                </div>

                {/* 대안이 둘 이상이면 고른다 — 화면은 언제나 기준선 대 하나, 두 칸이다 */}
                {all.length > 1 && (
                    <div className={styles.variants}>
                        {all.map(r => (
                            <button
                                type="button"
                                key={r.label}
                                className={`${styles.variantChip}${(after && r.label === after.label) ? ' ' + styles.variantOn : ''}`}
                                onClick={() => setPickedLabel(r.label)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                )}

                {onSaveToArchive && (
                    <button type="button" className={styles.saveBtn} onClick={() => onSaveToArchive(tab)}>
                        <FiBookmark size={12} /> {t('common:plan.saveToArchive')}
                    </button>
                )}
            </div>

            {tab.summary && <div className={styles.summary}>{tab.summary}</div>}
            {tab.verdict && tab.verdict.warnings && tab.verdict.warnings.length > 0 && (
                <div className={styles.warnings}>
                    <FiAlertTriangle size={12} />
                    <span>{tab.verdict.warnings.join(' · ')}</span>
                </div>
            )}

            <div className={styles.panes}>
                <SplitView isSplit renderClone={() => child(afterTab)}>
                    {child(beforeTab)}
                </SplitView>
            </div>
        </div>
    );
}

PlanCompare.propTypes = {
    /** `compare: { before, after, all, labels? }` 를 가진 실행계획 탭. labels = { before, after } 머리글 */
    tab: PropTypes.object.isRequired,
    statsConnectionId: PropTypes.string,
    onPlanNodeClick: PropTypes.func,
    onShowTableDDL: PropTypes.func,
    showPlanObjectDetail: PropTypes.bool,
    onSaveToArchive: PropTypes.func,
};

export default PlanCompare;
